"use client";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
} from "@/components/ui/dialog";
import { PlusIcon } from "@/components/ui/plus";
import { Dialog as RadixDialog } from "radix-ui";
import { cn } from "@/lib/utils";
import { BookOpenTextIcon } from "lucide-react";
import { Fragment, type ReactNode } from "react";

export type SearchDialogItem = {
  id: string;
  value: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  meta?: ReactNode;
};

export type SearchDialogGroup = {
  heading: string;
  items: SearchDialogItem[];
};

type SearchDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  placeholder?: string;
  emptyMessage?: string;
  groups: SearchDialogGroup[];
  onSelect: (id: string) => void;
  onCreate?: () => void;
  createLabel?: string;
  onLearn?: () => void;
  learnLabel?: string;
};

export function SearchDialog({
  open,
  onOpenChange,
  title = "Search",
  description,
  placeholder = "Search…",
  emptyMessage = "No results found.",
  groups,
  onSelect,
  onCreate,
  createLabel = "Create new",
  onLearn,
  learnLabel = "Learn",
}: SearchDialogProps) {
  const handleSelect = (id: string) => {
    onSelect(id);
    onOpenChange(false);
  };

  const nonEmpty = groups.filter((g) => g.items.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />

        {/* Dialog box */}
        <RadixDialog.Content
          className={cn(
            "fixed top-1/3 left-1/2 z-50 w-full max-w-[calc(100%-2rem)] -translate-x-1/2 translate-y-0",
            "overflow-hidden rounded-4xl bg-popover text-sm text-popover-foreground shadow-xl ring-1 ring-foreground/5",
            "outline-none sm:max-w-2xl",
            "duration-100 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          )}
        >
          <RadixDialog.Title className="sr-only">{title}</RadixDialog.Title>
          <RadixDialog.Description className="sr-only">
            {description ?? title}
          </RadixDialog.Description>
          <Command>
            <CommandInput placeholder={placeholder} />
            {onCreate && (
              <div className="border-b border-border p-1">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-2xl px-3 py-2 text-sm font-normal text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                  onClick={() => { onCreate(); onOpenChange(false); }}
                >
                  <PlusIcon size={14} />
                  New {createLabel}
                </button>
              </div>
            )}
            <CommandList className="max-h-112">
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              {nonEmpty.map((group, i) => (
                <Fragment key={group.heading}>
                  {i > 0 && <CommandSeparator key={`sep-${group.heading}`} />}
                  <CommandGroup heading={group.heading}>
                    {group.items.map((item) => {
                      const commandValue = `${i}:${item.id}`;

                      return (
                        <CommandItem
                          className="font-normal"
                          key={item.id}
                          value={commandValue}
                          keywords={[item.value, item.label, group.heading]}
                          onSelect={() => handleSelect(item.id)}
                        >
                          {item.icon}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate">{item.label}</span>
                            {item.description && (
                              <span className="block truncate text-xs text-muted-foreground">{item.description}</span>
                            )}
                          </span>
                          {item.meta}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </Fragment>
              ))}
            </CommandList>
          </Command>
          {onLearn && (
            <div className="flex justify-end border-t border-border px-3 py-2">
              <button
                type="button"
                className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
                onClick={() => { onLearn(); onOpenChange(false); }}
              >
                <BookOpenTextIcon className="size-3.5 shrink-0" />
                {learnLabel}
              </button>
            </div>
          )}
        </RadixDialog.Content>
      </DialogPortal>
    </Dialog>
  );
}
